import { useCallback, useEffect, useState } from "react";

const FAVORITE_ANIME_KEY = "riman.favorites.anime";
const FAVORITE_NEWS_KEY = "riman.favorites.news";

const readList = (key: string): string[] => {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeList = (key: string, value: string[]) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(new Event("riman-favorites-change"));
  } catch {
    // ignore quota errors
  }
};

export function useFavorites(kind: "anime" | "news" = "anime") {
  const key = kind === "anime" ? FAVORITE_ANIME_KEY : FAVORITE_NEWS_KEY;
  const [ids, setIds] = useState<string[]>(() => readList(key));

  useEffect(() => {
    const sync = () => setIds(readList(key));
    window.addEventListener("riman-favorites-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("riman-favorites-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, [key]);

  const toggle = useCallback(
    (id: string) => {
      const current = readList(key);
      const next = current.includes(id) ? current.filter((item) => item !== id) : [...current, id];
      writeList(key, next);
      setIds(next);
    },
    [key]
  );

  const isFavorite = useCallback((id: string) => ids.includes(id), [ids]);

  return { ids, toggle, isFavorite };
}